import React, { useState, useEffect } from 'react';
import { getAssetUrl } from '../../utils/assets';
import { UserProfile } from '../../types';
import { LogOut, Building2, Pencil, Sparkles } from 'lucide-react';
import { signOut } from '../../auth/authService';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../auth/LoginPage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { Modal } from '../common/Modal';

const isDev = import.meta.env.DEV;

// Reusing same app definitions as GlobalNav/LoginPage
const APPS_META = [
    {
        id: 'attendance',
        name: 'Control de Asistencia',
        description: 'Gestión de personal en tiempo real con reconocimiento facial.',
        logoUrl: getAssetUrl('/Logos/Logo CA-1.png'),
        url: isDev ? 'http://localhost:5182/control-asistencia/' : '/control-asistencia/',
    },
    {
        id: 'inventory',
        name: 'Control de Inventario',
        description: 'Administración de materiales y herramientas en obra.',
        logoUrl: getAssetUrl('/Logos/Logo CI-1.png'),
        url: isDev ? 'http://localhost:3000/control-inventario/' : '/control-inventario/',
    },
    {
        id: 'expenses',
        name: 'Gastos de Obra',
        description: 'Control detallado de costos de obras.',
        logoUrl: getAssetUrl('/Logos/Logo Gastos.png'),
        url: isDev ? 'http://localhost:3001/gastos/' : '/gastos/',
    },
    {
        id: 'presupuestos',
        name: 'Gestión de Presupuestos',
        description: 'Planificación superior. Control de partidas y desviaciones.',
        logoUrl: getAssetUrl('/Logos/Logo Presupuestos.png'),
        url: isDev ? 'http://localhost:5176/presupuestos/' : '/presupuestos/',
    },
    {
        id: 'core',
        name: 'Asistente Artificial Core',
        description: 'Tu copiloto inteligente. Predicciones y gestión automatizada.',
        logoUrl: getAssetUrl('/Logos/Logo IA-1.png'),
        url: '#',
    }
];

export interface InternalHubPageProps {
    user: UserProfile | null;
}

export const InternalHubPage: React.FC<InternalHubPageProps> = ({ user }) => {
    const navigate = useNavigate();
    const userRole = (user?.role || 'guest').toLowerCase();
    const userApps = user?.apps || [];
    const isOwner = userRole === 'owner';

    const [orgName, setOrgName] = useState<string>('');
    const [isOnboarded, setIsOnboarded] = useState<boolean>(true);
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const [companyInput, setCompanyInput] = useState('');
    const [savingCompany, setSavingCompany] = useState(false);

    // Fetch Organization Info
    useEffect(() => {
        const fetchOrg = async () => {
            if (!user?.orgId) return;
            try {
                const orgRef = doc(db, 'organizations', user.orgId);
                const snap = await getDoc(orgRef);
                if (snap.exists()) {
                    const data = snap.data() as any;
                    const name = data?.name || '';
                    const onboarded = data?.isOnboarded === true;
                    setOrgName(name);
                    setIsOnboarded(onboarded);

                    // Si es owner y no está onboarded, abrir modal de bienvenida
                    if (isOwner && !onboarded) {
                        setCompanyInput(name.startsWith('Despacho de') || name.startsWith('Empresa de') ? '' : name);
                        setOnboardingOpen(true);
                    }
                }
            } catch (err) {
                console.warn('Error leyendo organización en Hub:', err);
            }
        };
        fetchOrg();
    }, [user?.orgId, isOwner]);

    const handleSaveCompany = async () => {
        if (!companyInput.trim() || !user?.orgId) return;
        try {
            setSavingCompany(true);
            const orgRef = doc(db, 'organizations', user.orgId);
            await setDoc(orgRef, { name: companyInput.trim(), isOnboarded: true }, { merge: true });
            setOrgName(companyInput.trim());
            setIsOnboarded(true);
            setOnboardingOpen(false);
        } catch (err: any) {
            console.error('Error guardando empresa:', err);
            alert(err?.message || 'Error guardando el nombre de la empresa');
        } finally {
            setSavingCompany(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error("Error signing out: ", error);
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col bg-black text-white font-sans overflow-x-hidden"
            style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url('${getAssetUrl('/imagenes/Fondo Nuevo-1.png')}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
            }}
        >
            {/* Header / Navbar simplificado */}
            <nav
                className="bg-[length:100%_100%] bg-no-repeat bg-center py-2 px-6 sm:px-10 lg:px-16 fixed w-full z-50 top-0 transition-all"
                style={{ backgroundImage: `url('${getAssetUrl('/imagenes/Banner.png')}')` }}
            >
                <div className="container mx-auto max-w-[1600px] flex justify-between items-center relative z-50">
                    <a href={import.meta.env.DEV ? 'http://localhost:5180' : 'https://archicoreanalytic.com'} className="flex items-center shrink-0">
                        <img
                            src={getAssetUrl("/Logos/LOGO ArchiCore TextLateral Fondo Oscuro.png")}
                            alt="ArchiCore Analytic Logo"
                            className="h-16 sm:h-18 lg:h-20 w-auto object-contain [filter:drop-shadow(4px_4px_3px_black)] transition-all duration-200"
                        />
                    </a>

                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-red-500/20 border border-white/15 hover:border-red-500/30 text-gray-300 hover:text-red-400 transition-all text-xs sm:text-sm font-medium shrink-0 shadow-sm"
                        title="Cerrar Sesión"
                    >
                        <LogOut size={16} className="text-red-400 shrink-0" />
                        <span className="hidden sm:inline">Cerrar Sesión</span>
                    </button>
                </div>

                {/* Scanning Light Beam */}
                <div className="absolute bottom-0 left-0 w-full h-[2px] overflow-hidden no-print pointer-events-none">
                    <div className="absolute top-0 h-full w-1/2 animate-scan-beam blur-[2px]"></div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="flex-grow w-full max-w-5xl mx-auto px-6 pt-24 sm:pt-32 lg:pt-36 pb-12 flex flex-col items-center justify-center">
                <div className="text-center mb-10">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                        Hola, {user?.name?.split(' ')[0] || 'Usuario'}
                    </h1>

                    {/* Organization Pill / Welcome Subtitle */}
                    {orgName && (
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-3 shadow-md">
                            <Building2 size={16} className="text-cyan-400" />
                            <span className="text-sm font-semibold text-gray-200">{orgName}</span>
                            {isOwner && (
                                <button
                                    onClick={() => {
                                        setCompanyInput(orgName);
                                        setOnboardingOpen(true);
                                    }}
                                    className="p-1 hover:text-cyan-400 text-gray-400 transition-colors"
                                    title="Editar nombre de la empresa"
                                >
                                    <Pencil size={13} />
                                </button>
                            )}
                        </div>
                    )}

                    <p className="text-lg md:text-xl text-gray-300">
                        ¿A qué aplicación de ArchiCore deseas ingresar hoy?
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                    {APPS_META.map((app, index) => {
                        const isGodMode = userRole === 'owner' || userRole === 'admin';
                        const isOffice = userRole === 'office';
                        const isResident = userRole === 'resident' || userRole === 'supervisor';
                        const residentAllowedNames = ['attendance', 'inventory'];
                        const isAuxiliary = userRole === 'auxiliary';
                        const auxiliaryAllowedNames = ['inventory'];

                        let hasAccess = false;

                        if (isGodMode || isOffice) {
                            hasAccess = true;
                        } else if (isResident) {
                            hasAccess = residentAllowedNames.includes(app.id);
                        } else if (isAuxiliary) {
                            hasAccess = auxiliaryAllowedNames.includes(app.id);
                        } else {
                            hasAccess = userApps.includes(app.id);
                        }

                        const isAccessible = hasAccess;
                        const gridClass = index === APPS_META.length - 1 ? "md:col-span-2" : "";
                        const isAi = app.id === 'core';

                        return (
                            <a
                                key={app.id}
                                href={isAccessible ? app.url : undefined}
                                className={`relative backdrop-blur-xl border rounded-2xl p-6 flex flex-row items-center gap-5 transition-all duration-300 w-full justify-start ${gridClass}
                                    ${isAccessible
                                        ? 'bg-[#181b24]/75 border-white/15 hover:scale-[1.02] hover:bg-[#202531]/85 hover:border-cyan-400/50 group cursor-pointer shadow-xl'
                                        : 'bg-black/40 border-gray-800 opacity-60 cursor-not-allowed hidden'
                                    }`}
                                style={!isAccessible ? { display: 'none' } : {}}
                                title={isAccessible ? app.name : 'No tienes acceso a este módulo'}
                                onClick={(e) => {
                                    if (!isAccessible) {
                                        e.preventDefault();
                                    }
                                }}
                            >
                                {isAi ? (
                                    <div className="flex-shrink-0 w-20 h-20 relative flex items-center justify-center">
                                        <div className="absolute inset-1 bg-gradient-to-r from-cyan-400/30 to-purple-600/40 rounded-full blur-lg animate-pulse pointer-events-none group-hover:scale-125 transition-transform duration-500"></div>
                                        <div className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-black/60 p-1 border border-cyan-400/40 relative z-10 flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:border-cyan-300 transition-colors">
                                            <img
                                                src={app.logoUrl}
                                                alt={`${app.name} Logo`}
                                                className="w-full h-full object-contain drop-shadow-[0_0_10px_rgba(0,216,238,0.7)] group-hover:scale-110 transition-transform duration-300 rounded-full"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center">
                                        <img
                                            src={app.logoUrl}
                                            alt={`${app.name} Logo`}
                                            className={`max-w-full max-h-full object-contain transition-transform duration-300 ${
                                                isAccessible ? 'group-hover:scale-105 drop-shadow-[0_0_8px_rgba(255,255,255,0.2)]' : 'grayscale'
                                            }`}
                                        />
                                    </div>
                                )}
                                <div className="flex-1 text-left">
                                    <h3 className={`font-bold text-xl leading-tight mb-2 transition-colors flex items-center gap-2 ${isAccessible ? 'text-white group-hover:text-cyan-400' : 'text-gray-500'}`}>
                                        {isAi ? (
                                            <>
                                                Asistente Artificial <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-300">Core</span>
                                                <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/30 align-middle font-semibold">BETA</span>
                                            </>
                                        ) : (
                                            app.name
                                        )}
                                    </h3>
                                    <p className={`text-sm leading-snug ${isAccessible ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {app.description}
                                    </p>
                                </div>
                                {isAccessible && (
                                    <div className="absolute right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400">
                                            →
                                        </div>
                                    </div>
                                )}
                            </a>
                        );
                    })}
                </div>

                {/* Secondary Logout at bottom for ergonomic mobile access */}
                <div className="mt-12 text-center">
                    <button
                        onClick={handleLogout}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-red-500/15 border border-white/10 hover:border-red-500/30 text-gray-400 hover:text-red-400 transition-all text-sm font-medium shadow-sm"
                    >
                        <LogOut size={16} />
                        Cerrar Sesión de la Suite
                    </button>
                </div>
            </main>
            <Footer />

            {/* Onboarding / Edit Company Modal */}
            <Modal
                isOpen={onboardingOpen}
                onClose={() => {
                    if (isOnboarded) setOnboardingOpen(false);
                }}
                title={isOnboarded ? "Editar Nombre de la Empresa" : "¡Bienvenido a ArchiCore Analytic!"}
            >
                <div className="space-y-4 text-left">
                    {!isOnboarded && (
                        <div className="flex items-center gap-3 p-3 bg-cyan-50 rounded-xl border border-cyan-200">
                            <Sparkles className="text-cyan-600 shrink-0" size={24} />
                            <p className="text-xs text-cyan-900 leading-snug">
                                Personaliza el espacio de trabajo para tu equipo de obra, reportes financieros y cotizaciones.
                            </p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                            Nombre de tu Despacho o Constructora
                        </label>
                        <input
                            type="text"
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-cyan-500 focus:outline-none text-sm font-medium"
                            placeholder="Ej. BONNEL Arquitectura y Construcción"
                            value={companyInput}
                            onChange={(e) => setCompanyInput(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-3">
                        {isOnboarded && (
                            <button
                                onClick={() => setOnboardingOpen(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                        <button
                            onClick={handleSaveCompany}
                            disabled={savingCompany || !companyInput.trim()}
                            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold disabled:opacity-50 transition-colors flex items-center gap-2"
                        >
                            {savingCompany ? 'Guardando...' : (isOnboarded ? 'Guardar Cambios' : 'Guardar y Comenzar 🚀')}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
